import {
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	SubmenuTrigger,
} from '@react-spectrum/s2';
import type React from 'react';
import { Button as RACButton } from 'react-aria-components';

import { cn } from '@/utils/cn';

import type { ActiveFilter } from './campaignLibraryFilterUtils';

// `h-8` rather than vertical padding: the add control's only child can be a bare 14px icon,
// whose line box is shorter than a text pill's, so padding alone sizes the two differently.
export const pillClass = `cursor-pointer rounded-full border border-(--glass-border)
	bg-(--glass-bg) h-8 px-3 text-[0.8125rem] flex items-center gap-1.5
	transition-[background,border-color,color] duration-150`;

export type DimensionConfig = {
	key: string;
	label: string;
	options: { label: string; value: string }[];
};

const findDimensionLabel = (
	dimensions: DimensionConfig[],
	key: string,
): string => dimensions.find((d) => d.key === key)?.label ?? key;

const findValueLabel = (
	dimensions: DimensionConfig[],
	key: string,
	value: string,
): string => {
	const dim = dimensions.find((d) => d.key === key);
	return dim?.options.find((o) => o.value === value)?.label ?? value;
};

const FilterPill = ({
	filter,
	dimensions,
	onRemove,
}: {
	filter: ActiveFilter;
	dimensions: DimensionConfig[];
	onRemove: () => void;
}) => (
	<span className={cn(pillClass, 'text-(--text-primary)')}>
		<span className="text-(--text-muted)">
			{findDimensionLabel(dimensions, filter.dimension)}
		</span>
		<span className="text-(--text-muted)">is</span>
		<span>{findValueLabel(dimensions, filter.dimension, filter.value)}</span>
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				onRemove();
			}}
			className="ml-0.5 text-(--text-muted) hover:text-(--text-primary)"
		>
			&times;
		</button>
	</span>
);

const HamburgerIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 14 14"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
	>
		<path d="M1.5 3.5h11M3.5 7h7M5.5 10.5h3" />
	</svg>
);

const PlusIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 14 14"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
	>
		<line x1="7" y1="3" x2="7" y2="11" />
		<line x1="3" y1="7" x2="11" y2="7" />
	</svg>
);

const FilterButton = <F extends ActiveFilter>({
	filters,
	dimensions,
	onAdd,
}: {
	filters: F[];
	dimensions: DimensionConfig[];
	onAdd: (filter: F) => void;
}) => {
	const hasFilters = filters.length > 0;

	const handleAction = (dim: DimensionConfig) => (key: React.Key) => {
		onAdd({ dimension: dim.key, value: String(key) } as F);
	};

	const renderOptions = (dim: DimensionConfig) =>
		dim.options.map((opt) => (
			<MenuItem
				key={opt.value}
				id={opt.value}
				isDisabled={filters.some(
					(f) => f.dimension === dim.key && f.value === opt.value,
				)}
			>
				{opt.label}
			</MenuItem>
		));

	return (
		<MenuTrigger>
			<RACButton
				className={cn(
					pillClass,
					`border-(--glass-border) bg-transparent text-(--text-muted)
					hover:border-(--glass-highlight) hover:text-(--text-secondary)`,
					// Icon-only: a circle the same size as a pill's height, not a wide lozenge.
					hasFilters && 'w-8 justify-center px-0',
				)}
			>
				{hasFilters ? (
					<PlusIcon />
				) : (
					<>
						<HamburgerIcon />
						Filter
					</>
				)}
			</RACButton>
			<Popover>
				{dimensions.length > 1 ? (
					<Menu>
						{dimensions.map((dim) => (
							<SubmenuTrigger key={dim.key}>
								<MenuItem id={dim.key}>{dim.label}</MenuItem>
								<Menu onAction={handleAction(dim)}>{renderOptions(dim)}</Menu>
							</SubmenuTrigger>
						))}
					</Menu>
				) : (
					<Menu onAction={handleAction(dimensions[0])}>
						{renderOptions(dimensions[0])}
					</Menu>
				)}
			</Popover>
		</MenuTrigger>
	);
};

export const FilterToolbar = <F extends ActiveFilter>({
	filters,
	dimensions,
	onAdd,
	onRemove,
}: {
	filters: F[];
	dimensions: DimensionConfig[];
	onAdd: (filter: F) => void;
	onRemove: (filter: F) => void;
}) => (
	<div className="flex items-start gap-2">
		<div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
			{filters.map((f) => (
				<FilterPill
					key={`${f.dimension}-${f.value}`}
					filter={f}
					dimensions={dimensions}
					onRemove={() => onRemove(f)}
				/>
			))}
		</div>
		<div className="shrink-0">
			<FilterButton filters={filters} dimensions={dimensions} onAdd={onAdd} />
		</div>
	</div>
);
